import type { TFunction } from 'i18next';
import type { CreateAnalysisRequestDto } from '@inference.engine/api-client/web';

export type AnalysisProvider = NonNullable<CreateAnalysisRequestDto['provider']>;

export interface AnalysisModelOption {
  model: string;
  provider: AnalysisProvider;
  label: string;
  hint: string;
}

/**
 * Owns the model copy users see. Ids mirror PROVIDER_MODELS in the API's analysis.dto.ts,
 * which validates them on create; the labels and hints live only here.
 */
export const ANALYSIS_MODEL_ENTRIES = [
  {
    model: 'deep-research-preview-04-2026',
    provider: 'gemini',
    labelKey: 'analysis.analysisModels.geminiDeepResearchLabel',
    hintKey: 'analysis.analysisModels.geminiDeepResearchHint',
  },
  {
    model: 'gpt-5.6-sol',
    provider: 'openai',
    labelKey: 'analysis.analysisModels.openaiGpt56SolLabel',
    hintKey: 'analysis.analysisModels.openaiGpt56SolHint',
  },
] as const satisfies readonly {
  model: string;
  provider: AnalysisProvider;
  labelKey: string;
  hintKey: string;
}[];

type AnalysisModelEntry = (typeof ANALYSIS_MODEL_ENTRIES)[number];

/** Mirrors the server's default. Typed off the table so a drifted id fails tsc. */
export const DEFAULT_ANALYSIS_MODEL: AnalysisModelEntry['model'] = 'gpt-5.6-sol';

/** Resolves labels at call time; a module-scope constant would freeze them at import. */
export const buildAnalysisModelOptions = (
  t: TFunction<'analysis'>,
): readonly AnalysisModelOption[] =>
  ANALYSIS_MODEL_ENTRIES.map(({ model, provider, labelKey, hintKey }) => ({
    model,
    provider,
    label: t(labelKey),
    hint: t(hintKey),
  }));

export const findAnalysisModelEntry = (model: string): AnalysisModelEntry | undefined =>
  ANALYSIS_MODEL_ENTRIES.find((entry) => entry.model === model);

export interface AnalysisProviderOption {
  provider: AnalysisProvider;
  label: string;
}

/** Keyed off AnalysisProvider, so a provider the API adds fails tsc until it gets copy. */
const PROVIDER_LABEL_KEYS = {
  openai: 'analysis.analysisProviders.openaiLabel',
  gemini: 'analysis.analysisProviders.geminiLabel',
} as const satisfies Record<AnalysisProvider, string>;

/**
 * The redesigned step picks a provider rather than a model, so each option resolves to
 * that provider's single registered model. A provider with two models would need the
 * model-level picker back.
 */
export const buildAnalysisProviderOptions = (
  t: TFunction<'analysis'>,
): readonly AnalysisProviderOption[] =>
  [...new Set(ANALYSIS_MODEL_ENTRIES.map((entry) => entry.provider))].map((provider) => ({
    provider,
    label: t(PROVIDER_LABEL_KEYS[provider]),
  }));

export const findAnalysisModelEntryByProvider = (
  provider: AnalysisProvider,
): AnalysisModelEntry | undefined =>
  ANALYSIS_MODEL_ENTRIES.find((entry) => entry.provider === provider);

export const DEFAULT_ANALYSIS_PROVIDER: AnalysisProvider = 'openai';
